import { useState, useEffect, useRef } from 'react'
import type { CarouselApi } from '@/components/ui/carousel'
import { FOCUSABLE_SELECTOR } from '@/lib/focus'

const FOCUS_OBSERVER_TIMEOUT_MS = 2000

/**
 * Hook to manage carousel synchronization with context step
 */
export function useCarouselSync(currentStep: number, open: boolean) {
  const [api, setApi] = useState<CarouselApi>()
  const isInitializedRef = useRef(false)
  const focusObserverRef = useRef<MutationObserver | null>(null)
  const focusObserverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Initialize carousel when it mounts
  useEffect(() => {
    if (!api || !open) return

    isInitializedRef.current = false
    api.scrollTo(0, true)

    // Mark as initialized after a rAF. If we're already at slide 0, Embla
    // won't fire 'settle' for an instant scroll, so we can't rely on that
    // event. rAF fires after all React effects have run (including the nav
    // effect for step=0), so it correctly separates init from user navigation.
    const raf = requestAnimationFrame(() => {
      isInitializedRef.current = true
    })
    return () => cancelAnimationFrame(raf)
  }, [api, open])

  // Sync carousel position when currentStep changes
  useEffect(() => {
    if (!api || !open) return

    api.scrollTo(currentStep, false)

    // The modal sets initial focus; only move focus after the user navigates.
    if (!isInitializedRef.current) return

    const onSettle = () => {
      api.off('settle', onSettle)

      const slides = api.rootNode()?.querySelectorAll('[data-slot="carousel-item"]')
      const targetSlide = slides?.[currentStep]
      if (!targetSlide) return

      const focusFirstInSlide = () => {
        const focusable = targetSlide.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
        focusable?.focus()
      }

      if (!targetSlide.hasAttribute('inert')) {
        // React has already committed its re-render — focus immediately.
        focusFirstInSlide()
        return
      }

      // React hasn't committed yet — watch for the inert attribute being removed.
      // MutationObserver fires as a microtask immediately after React's DOM commit,
      // which is faster than waiting a full animation frame.
      focusObserverRef.current?.disconnect()
      if (focusObserverTimeoutRef.current !== null) {
        clearTimeout(focusObserverTimeoutRef.current)
      }
      const observer = new MutationObserver(() => {
        if (!targetSlide.hasAttribute('inert')) {
          clearTimeout(focusObserverTimeoutRef.current ?? undefined)
          focusObserverTimeoutRef.current = null
          observer.disconnect()
          focusObserverRef.current = null
          focusFirstInSlide()
        }
      })
      focusObserverRef.current = observer
      observer.observe(targetSlide, { attributes: true, attributeFilter: ['inert'] })
      // Safety net: if inert is never removed (e.g. a bug in slide-count logic),
      // disconnect after a reasonable window so the observer can't steal focus late.
      focusObserverTimeoutRef.current = setTimeout(() => {
        observer.disconnect()
        focusObserverRef.current = null
        focusObserverTimeoutRef.current = null
        if (!targetSlide.hasAttribute('inert')) {
          focusFirstInSlide()
        }
      }, FOCUS_OBSERVER_TIMEOUT_MS)
    }

    api.on('settle', onSettle)
    return () => {
      api.off('settle', onSettle)
      focusObserverRef.current?.disconnect()
      focusObserverRef.current = null
      if (focusObserverTimeoutRef.current !== null) {
        clearTimeout(focusObserverTimeoutRef.current)
        focusObserverTimeoutRef.current = null
      }
    }
  }, [api, currentStep, open])

  return { api, setApi }
}
